import { useMemo } from 'react';
import { ProductInterface } from '../../../services/exports/Interfaces';
import { WithActualPrice } from '../../../providers/MenuProvider';
import { BasketLineItem } from '../../../store/types';
import useBasket from '../../../hooks/basket/useBasket';
import usePreviewableOffers from './usePreviewableOffers';

export interface ProductPreviewPrice {
  actualPrice: number;
  originalPrice: number;
}

type PreviewableProduct = WithActualPrice<ProductInterface> & { order_line_item?: BasketLineItem };

/**
 * The price a menu product would cost if it were added to the basket right now, offers included.
 *
 * Menu cards used to render the plain order-method price, so an offer showed up as a badge but
 * never as money — the discount only appeared once the item was already in the basket. This closes
 * that gap: if adding the item would make an offer apply, the discounted price shows immediately.
 *
 * "Order again" cards carry the past order's line item (`order_line_item`: product, modifiers,
 * note), and tapping one re-adds exactly that. So they are priced from that line item — current
 * base price plus the current price of the stored modifiers, offers applied — which is what the
 * product modal and the basket will show. The historical amounts of the past order are never used.
 */
export default function useProductPreviewPrice(product: WithActualPrice<ProductInterface>): ProductPreviewPrice {
  const { compilePreviewProduct, compilePreviewItem, originalItems } = useBasket();
  const { offers, discountedProductIds, isEveryProductDiscounted } = usePreviewableOffers();

  const orderLineItem = (product as PreviewableProduct)?.order_line_item;

  const reorderItem = useMemo(
    () => (orderLineItem ? compilePreviewItem(orderLineItem, offers) : null),
    // `compilePreviewItem` is intentionally not a dependency: the basket context object is a
    // fresh literal on every render, which would defeat this memo entirely.
    [orderLineItem, offers, originalItems],
  );

  // Ordered cheapest-first. The last guard is what keeps this affordable on a long menu: the
  // category list is only coarsely windowed, so a few hundred cards can be mounted at once and all
  // but the handful an offer targets must cost a single Set lookup.
  const shouldPreview =
    !orderLineItem &&
    !!product?.id &&
    !!product?.actual_price &&
    !!offers.length &&
    (isEveryProductDiscounted || discountedProductIds.has(product.id));

  const preview = useMemo(
    () => (shouldPreview ? compilePreviewProduct(product, offers) : null),
    // `compilePreviewProduct` is intentionally not a dependency, see above.
    [shouldPreview, product, offers, originalItems],
  );

  if (reorderItem) {
    return {
      actualPrice: reorderItem.actual_price,
      originalPrice: reorderItem.original_price,
    };
  }

  return {
    actualPrice: preview?.actual_price ?? product?.actual_price,
    originalPrice: product?.base_price,
  };
}
